import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import SFButton from "../../../button";
import SFAsyncActionButton from "../../../button/asyncActionButton";
import { SFStack } from "../../../grid";
import SFTypography from "../../../typography";
import getStyled from "../../../../hooks/getStyled";
import { SFArrowDownIcon } from "../../../icons";
import IconTypeTooltip from "../../components/iconTypeTooltip";

const { styled } = getStyled();

const ButtonActionWrapper = styled(SFStack)(() => ({
  height: "48px",
  padding: "10px 0px",
  "& .MuiButtonBase-root": {
    height: "30px",
    "& .MuiTypography-root": {
      // The label of the button should not wrap
      textWrap: "nowrap",
    },
  },
}));

const EndIcon = ({ actionDef }) => {
  if (actionDef.buttonProps?.dropdown) {
    return <SFArrowDownIcon />;
  }
  return actionDef.buttonProps?.icon;
};

const ButtonAction = ({ actionDef, disableTooltip = false }) => {
  const { t } = useTranslation();

  // Determine if we need to wrap the button with a tooltip for the disabled reason
  const shouldShowDisabledReason =
    actionDef.disabled && actionDef.buttonProps?.disabledReason;

  // Create the button component based on async requirements
  const buttonComponent =
    actionDef.buttonProps?.async && !actionDef.confirmation ? (
      // If the action is async and there is no confirmation, render the async action button
      // If the action is async but a confirmation is required, render a regular button, because the confirmation will handle the async action
      <SFAsyncActionButton
        variant={actionDef.buttonProps?.variant}
        onError={actionDef.buttonProps?.onError}
        onClick={actionDef.onClick}
        {...(actionDef.buttonProps?.icon &&
          actionDef.buttonProps?.iconPlacement === "start" && {
          startIcon: actionDef.buttonProps.icon,
        })}
        {...((actionDef.buttonProps?.dropdown ||
          (actionDef.buttonProps?.icon &&
            actionDef.buttonProps?.iconPlacement === "end")) && {
          endIcon: <EndIcon actionDef={actionDef} />,
        })}
        disabled={actionDef.disabled}
        arial-label={actionDef.buttonProps?.label}
      >
        {actionDef.buttonProps?.label ?? t("generic.blank")}
      </SFAsyncActionButton>
    ) : (
      <SFButton
        variant={actionDef.buttonProps?.variant}
        onClick={actionDef.onClick}
        {...(actionDef.buttonProps?.icon &&
          actionDef.buttonProps?.iconPlacement === "start" && {
          icon: actionDef.buttonProps.icon,
        })}
        {...((actionDef.buttonProps?.dropdown ||
          (actionDef.buttonProps?.icon &&
            actionDef.buttonProps?.iconPlacement === "end")) && {
          endIcon: <EndIcon actionDef={actionDef} />,
        })}
        disabled={actionDef.disabled}
        arial-label={actionDef.buttonProps?.label}
      >
        <SFTypography variant="body2">
          {actionDef.buttonProps?.label ?? t("generic.blank")}
        </SFTypography>
      </SFButton>
    );

  return (
    <ButtonActionWrapper alignItems="center" justifyContent="center">
      {shouldShowDisabledReason ? (
        <IconTypeTooltip
          tooltip={actionDef.buttonProps.disabledReason}
          wrapChildrenWithSpan={true}
          disableTooltip={disableTooltip}
        >
          {buttonComponent}
        </IconTypeTooltip>
      ) : (
        buttonComponent
      )}
    </ButtonActionWrapper>
  );
};

export default ButtonAction;

ButtonAction.propTypes = {
  actionDef: PropTypes.shape({
    // The name of the action
    name: PropTypes.string.isRequired,
    // The function to call when the button is clicked
    onClick: PropTypes.func.isRequired,
    // The props to pass to the button
    buttonProps: PropTypes.shape({
      // The label of the button
      label: PropTypes.string.isRequired,
      // The icon to display on the button
      icon: PropTypes.element,
      // The icon placement of the button
      iconPlacement: PropTypes.oneOf(["start", "end"]),
      // The variant of the button
      variant: PropTypes.oneOf(["primary", "secondary", "utility"]),
      // If the button is dropdown
      dropdown: PropTypes.bool,
      // Whether the button is async
      async: PropTypes.bool,
      // Callback fired when the async onClick has an error
      onError: PropTypes.func,
      // The reason why the button is disabled (shown as tooltip when hovering)
      disabledReason: PropTypes.string,
    }),
  }),
};
